import { useCallback, useEffect, useState } from "react";

import { useLocalStorage } from "usehooks-ts";

const STORAGE_KEY = "copilot-committed-agent-versions";
const TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

type VersionEntry = {
  versionId: string;
  committedAt: number;
};

type VersionMap = Record<string, VersionEntry>;

const pruneExpired = (map: VersionMap): VersionMap => {
  const now = Date.now();
  const pruned: VersionMap = {};
  for (const [key, entry] of Object.entries(map)) {
    if (now - entry.committedAt < TTL_MS) {
      pruned[key] = entry;
    }
  }
  return pruned;
};

type UseAgentVersionChangeParams = {
  caseId: string;
  latestVersionId: string | undefined;
};

type UseAgentVersionChangeResult = {
  activeVersionId: string | undefined;
  hasVersionChanged: boolean;
  acceptNewVersion: () => void;
  dismissChange: () => void;
};

export const useAgentVersionChange = ({
  caseId,
  latestVersionId,
}: UseAgentVersionChangeParams): UseAgentVersionChangeResult => {
  const [storedVersions, setStoredVersions] = useLocalStorage<VersionMap>(
    STORAGE_KEY,
    {},
  );

  const committedVersionId = storedVersions[caseId]?.versionId;

  const [dismissedVersionId, setDismissedVersionId] = useState<string>();

  // First visit for this case: commit the initial version
  useEffect(() => {
    if (!latestVersionId || committedVersionId) return;

    setStoredVersions((prev) => ({
      ...pruneExpired(prev),
      [caseId]: { versionId: latestVersionId, committedAt: Date.now() },
    }));
  }, [latestVersionId, committedVersionId, caseId, setStoredVersions]);

  const hasVersionChanged =
    !!committedVersionId &&
    !!latestVersionId &&
    latestVersionId !== committedVersionId &&
    latestVersionId !== dismissedVersionId;

  const acceptNewVersion = useCallback(() => {
    if (!latestVersionId) return;

    setStoredVersions((prev) => ({
      ...pruneExpired(prev),
      [caseId]: { versionId: latestVersionId, committedAt: Date.now() },
    }));
    setDismissedVersionId(undefined);
  }, [latestVersionId, caseId, setStoredVersions]);

  const dismissChange = useCallback(() => {
    setDismissedVersionId(latestVersionId);
  }, [latestVersionId]);

  return {
    activeVersionId: committedVersionId,
    hasVersionChanged,
    acceptNewVersion,
    dismissChange,
  };
};
